import * as React from 'react'
import styled, { css } from 'styled-components'
import Typography, { TYPOGRAPHY_ICON_SIZE_CSS_VAR, TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from './Typography'
import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { setCSSVar } from 'lib/theme/themeUtils'

const ArabicLetterSpacing = css`
  &:lang(ar) {
    letter-spacing: 0;
  }
`

export const Heading1Style = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '34px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(40))}
  font-family: ${props => props.theme.font.secondary.family};
  letter-spacing: ${props => props.theme.typography.heading.one.letterSpacing};
  ${ArabicLetterSpacing}
  font-size: 32px;

  &, &:is(b, strong), b, strong {
    font-weight: ${props => props.theme.font.secondary.weight[props.theme.typography.heading.one.weight]};
  }

  ${mediaQueryUp.tablet} {
    ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '48px')}
    font-size: 40px;
  }
`

export const Heading1AltStyle = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '34px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(40))}
  font-family: ${props => props.theme.font.secondaryAlternate.family};
  letter-spacing: ${props => props.theme.typography.heading.oneAlt.letterSpacing};
  ${ArabicLetterSpacing}
  font-size: 32px;

  &, &:is(b, strong), b, strong {
    font-weight: ${props => props.theme.font.secondaryAlternate.weight[props.theme.typography.heading.oneAlt.weight]};
  }

  ${mediaQueryUp.tablet} {
    ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '48px')}
    font-size: 40px;
  }
`

export const Heading2Style = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '28px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(40))}
  font-family: ${props => props.theme.font.secondary.family};
  letter-spacing: ${props => props.theme.typography.heading.two.letterSpacing};
  ${ArabicLetterSpacing}
  font-size: 24px;

  &, &:is(b, strong), b, strong {
    font-weight: ${props => props.theme.font.secondary.weight[props.theme.typography.heading.two.weight]};
  }

  ${mediaQueryUp.tablet} {
    ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '34px')}
    font-size: 32px;
  }
`

export const Heading2AltStyle = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '28px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(40))}
  font-family: ${props => props.theme.font.secondaryAlternate.family};
  letter-spacing: ${props => props.theme.typography.heading.twoAlt.letterSpacing};
  ${ArabicLetterSpacing}
  font-style: ${props => props.theme.font.secondaryAlternate.fontStyle};
  font-size: 24px;

  &, &:is(b, strong), b, strong {
    font-weight: ${props => props.theme.font.secondaryAlternate.weight[props.theme.typography.heading.twoAlt.weight]};
  }

  ${mediaQueryUp.tablet} {
    ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '34px')}
    font-size: 32px;
  }
`

export const Heading3Style = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '24px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(24))}
  font-family: ${props => props.theme.font.secondary.family};
  letter-spacing: ${props => props.theme.typography.heading.three.letterSpacing};
  ${ArabicLetterSpacing}
  font-size: 20px;

  &, &:is(b, strong), b, strong {
    font-weight: ${props => props.theme.font.secondary.weight[props.theme.typography.heading.three.weight]};
  }

  ${mediaQueryUp.tablet} {
    ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '28px')}
    font-size: 24px;
  }
`

export const Heading3AltStyle = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '24px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(24))}
  font-family: ${props => props.theme.font.secondaryAlternate.family};
  letter-spacing: ${props => props.theme.typography.heading.threeAlt.letterSpacing};
  ${ArabicLetterSpacing}
  font-size: 20px;

  &, &:is(b, strong), b, strong {
    font-weight: ${props => props.theme.font.secondaryAlternate.weight[props.theme.typography.heading.threeAlt.weight]};
  }

  ${mediaQueryUp.tablet} {
    ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '28px')}
    font-size: 24px;
  }
`

export const Heading4Style = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '21px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(24))}
  font-family: ${props => props.theme.font.secondary.family};
  letter-spacing: ${props => props.theme.typography.heading.four.letterSpacing};
  ${ArabicLetterSpacing}
  font-size: 18px;

  &, &:is(b, strong), b, strong {
    font-weight: ${props => props.theme.font.secondary.weight[props.theme.typography.heading.four.weight]};
  }

  ${mediaQueryUp.tablet} {
    ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '24px')}
    font-size: 20px;
  }
`

export const Heading5Style = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '22px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(20))}
  font-family: ${props => props.theme.font.secondary.family};
  letter-spacing: ${props => props.theme.typography.heading.five.letterSpacing};
  ${ArabicLetterSpacing}
  font-size: 16px;

  &, &:is(b, strong), b, strong {
    font-weight: ${props => props.theme.font.secondary.weight[props.theme.typography.heading.five.weight]};
  }

  ${mediaQueryUp.tablet} {
    ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '24px')}
    font-size: 18px;
  }
`

export const Heading6Style = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '19px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(16))}
  font-family: ${props => props.theme.font.secondary.family};
  letter-spacing: ${props => props.theme.typography.heading.six.letterSpacing};
  ${ArabicLetterSpacing}
  font-size: 14px;

  &, &:is(b, strong), b, strong {
    font-weight: ${props => props.theme.font.secondary.weight[props.theme.typography.heading.six.weight]};
  }

  ${mediaQueryUp.tablet} {
    ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '19px')}
    font-size: 16px;
  }
`

/*
  Font sizes are express in px as they are not to be affected by the
  20% REM font decrease at mobile sizes. These font sizes are absolute sizes.
*/
const Element = styled(Typography)`
  /* reset font-style */
  &, &:is(i, em), i, em {
    font-style: normal;
  }

  &.variant-heading1 {
    ${Heading1Style}
  }

  &.variant-heading1alt,
  &.variant-heading1alt:is(i, em),
  &.variant-heading1alt i,
  &.variant-heading1alt em,
  &.variant-heading1:is(i, em),
  &.variant-heading1 i,
  &.variant-heading1 em {
    ${Heading1AltStyle}
  }

  &.variant-heading2 {
    ${Heading2Style}
  }

  &.variant-heading2alt,
  &.variant-heading2alt:is(i, em),
  &.variant-heading2alt i,
  &.variant-heading2alt em,
  &.variant-heading2:is(i, em),
  &.variant-heading2 i,
  &.variant-heading2 em {
    ${Heading2AltStyle}
  }

  &.variant-heading3 {
    ${Heading3Style}
  }

  &.variant-heading3alt,
  &.variant-heading3alt:is(i, em),
  &.variant-heading3alt i,
  &.variant-heading3alt em,
  &.variant-heading3:is(i, em),
  &.variant-heading3 i,
  &.variant-heading3 em {
    ${Heading3AltStyle}
  }

  &.variant-heading4 {
    ${Heading4Style}
  }

  &.variant-heading5 {
    ${Heading5Style}
  }

  &.variant-heading6 {
    ${Heading6Style}
  }
`

interface Props extends Omit<React.ComponentProps<typeof Typography>, 'forwardedAs'> {
  variant: 'heading1' | 'heading1alt' | 'heading2' | 'heading2alt' | 'heading3' | 'heading3alt' | 'heading4' | 'heading5' | 'heading6'
}

function Heading(props: Props) {
  const { variant, as, className, ...rest } = props

  return <Element
    {...rest}
    forwardedAs={as}
    className={clsx(className, `variant-${variant}`)}
  />
}

export default Heading
